import { fabric } from 'fabric';
import { round } from 'shared/utils/round';
import { normalizeGradientAngle } from './gradient-stops';

/** Точка градиента: положение 0–1 вдоль линии градиента и любой CSS-цвет (в каталоге бывают `rgba(…)`). */
export interface GradientStop {
  offset: number;
  color: string;
}

/**
 * Линейный градиент так, как его видит человек: угол как в CSS `linear-gradient` (0° — снизу вверх, 90° — слева
 * направо) и точки. Источник правды — сам `object.fill`; модель только читается из него и собирается обратно.
 */
export interface LinearGradientModel {
  angle: number;
  stops: GradientStop[];
}

/** Поля `fabric.Gradient`, которых нет в `@types/fabric`. */
interface GradientLike {
  type?: string;
  coords?: { x1?: number; y1?: number; x2?: number; y2?: number };
  colorStops?: GradientStop[];
  gradientUnits?: 'pixels' | 'percentage';
  gradientTransform?: number[] | null;
  offsetX?: number;
  offsetY?: number;
}

/** Любой градиент Fabric (линейный или радиальный) — в отличие от строки цвета и узора. */
export function isGradientFill(fill: unknown): boolean {
  return typeof fill === 'object' && fill !== null && Array.isArray((fill as GradientLike).colorStops);
}

/**
 * Градиент в пикселях объекта: при смене `width`/`height` его надо пересобрать (`resizeLinearGradient`), процентный
 * растягивается сам. У Fabric `gradientUnits` по умолчанию `'pixels'`, поэтому отсутствие значения — тоже пиксели.
 */
export function isPixelGradient(fill: unknown): boolean {
  return isGradientFill(fill) && (fill as GradientLike).gradientUnits !== 'percentage';
}

/**
 * Модель из `fill` объекта размером `width × height` (без масштаба). Линия градиента проецируется на CSS-линию того
 * же угла, поэтому точки, лежащие не через центр (горизонталь с `y = 0`), читаются так же, как выглядят. Радиальный,
 * с `gradientTransform` или со смещением — `undefined`: редактор такой не меняет.
 */
export function readLinearGradient(fill: unknown, width: number, height: number): LinearGradientModel | undefined {
  if (!isGradientFill(fill)) return undefined;
  const gradient = fill as GradientLike;
  if (gradient.type !== 'linear' || gradient.gradientTransform || gradient.offsetX || gradient.offsetY) return undefined;

  const unitX = gradient.gradientUnits === 'percentage' ? width : 1;
  const unitY = gradient.gradientUnits === 'percentage' ? height : 1;
  const { x1 = 0, y1 = 0, x2 = 0, y2 = 0 } = gradient.coords ?? {};
  const dx = (x2 - x1) * unitX;
  const dy = (y2 - y1) * unitY;
  if (!dx && !dy) return undefined;

  const angle = normalizeGradientAngle((Math.atan2(dx, -dy) * 180) / Math.PI);
  const line = cssGradientLine(angle, width, height);
  const along = (x: number, y: number) =>
    line.length ? ((x - line.start.x) * line.direction.x + (y - line.start.y) * line.direction.y) / line.length : 0;
  const from = along(x1 * unitX, y1 * unitY);
  const to = along(x2 * unitX, y2 * unitY);

  const stops = (gradient.colorStops ?? [])
    .map((stop) => ({ offset: round(from + stop.offset * (to - from), 4), color: stop.color }))
    .sort((a, b) => a.offset - b.offset);

  return { angle: round(angle, 4), stops };
}

/** `fabric.Gradient` в пикселях объекта: линия через центр, длина — проекция рамки на направление (как в CSS). */
export function buildLinearGradient(model: LinearGradientModel, width: number, height: number): fabric.Gradient {
  const line = cssGradientLine(model.angle, width, height);
  const end = { x: line.start.x + line.direction.x * line.length, y: line.start.y + line.direction.y * line.length };

  return new fabric.Gradient({
    type: 'linear',
    gradientUnits: 'pixels',
    coords: { x1: toCoord(line.start.x), y1: toCoord(line.start.y), x2: toCoord(end.x), y2: toCoord(end.y) },
    colorStops: [...model.stops].sort((a, b) => a.offset - b.offset).map((stop) => ({ offset: stop.offset, color: stop.color })),
  });
}

/**
 * Тот же градиент для нового размера объекта: пиксельные координаты иначе перестают покрывать фигуру. Нечитаемый
 * градиент и не-градиент возвращаются как есть.
 */
export function resizeLinearGradient<T>(fill: T, from: Size, to: Size): T | fabric.Gradient {
  const model = readLinearGradient(fill, from.width, from.height);
  return model ? buildLinearGradient(model, to.width, to.height) : fill;
}

/**
 * Градиент, в который превращается сплошной цвет при выборе «Градиент»: слева направо от этого цвета к нему же
 * прозрачному — так собраны полоски и плашки каталога.
 */
export function defaultLinearGradient(color: string): LinearGradientModel {
  const transparent = new fabric.Color(color);
  transparent.setAlpha(0);
  return {
    angle: 90,
    stops: [
      { offset: 0, color },
      { offset: 1, color: '#' + transparent.toHexa() },
    ],
  };
}

/** Цвет первой точки — им градиент становится при переходе на сплошной цвет. */
export function firstStopColor(fill: unknown): string | undefined {
  if (!isGradientFill(fill)) return undefined;
  const stops = [...((fill as GradientLike).colorStops ?? [])].sort((a, b) => a.offset - b.offset);
  return stops[0]?.color;
}

interface Size {
  width: number;
  height: number;
}

/** Шум `sin`/`cos` на осях (`6e-17`) и `-0` не попадают в сохранённый JSON: ось каталога даёт ровно `0`/`w`/`h/2`. */
function toCoord(value: number): number {
  return round(value, 4) + 0;
}

/** Линия CSS `linear-gradient`: длина `|W·sin A| + |H·cos A|`, середина в центре рамки. */
function cssGradientLine(angle: number, width: number, height: number) {
  const radians = (angle * Math.PI) / 180;
  const direction = { x: Math.sin(radians), y: -Math.cos(radians) };
  const length = Math.abs(width * direction.x) + Math.abs(height * direction.y);
  const start = { x: width / 2 - (direction.x * length) / 2, y: height / 2 - (direction.y * length) / 2 };
  return { direction, length, start };
}
